import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../lib/api";
import { Button, Input, Card, CardHeader, CardContent, useToast, Toolbar } from "@dawa/ui";
import { CopyButton } from "../components/CopyButton";

interface Result {
  analysis?: any;
  ideas?: any;
  competitors?: any;
}

export default function KeywordResearch() {
  const [keyword, setKeyword] = useState("");
  const [result, setResult] = useState<Result | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addToast, Toasts } = useToast();
  const SUGGESTIONS = useMemo(() => [
    "crm pour freelance",
    "meilleure chaise ergonomique 2025",
    "audit seo gratuit",
    "outil planification réseaux sociaux",
  ], []);
  function toRows(data: any): { headers: string[]; rows: string[][] } | null {
    try {
      if (!data) return null;
      if (Array.isArray(data)) {
        if (data.length === 0) return { headers: ["value"], rows: [] };
        if (typeof data[0] === "string") return { headers: ["value"], rows: data.map((s) => [String(s)]) };
        if (typeof data[0] === "object") {
          const headers = Array.from(new Set(data.flatMap((o) => Object.keys(o || {}))));
          const rows = data.map((o) => headers.map((h) => JSON.stringify((o || {})[h] ?? "")));
          return { headers, rows };
        }
      }
      if (typeof data === "object") {
        const entries = Object.entries(data);
        return { headers: ["key", "value"], rows: entries.map(([k, v]) => [k, JSON.stringify(v)]) };
      }
      return { headers: ["value"], rows: [[String(data)]] };
    } catch {
      return null;
    }
  }
  function toCSV(tbl: { headers: string[]; rows: string[][] }) {
    const esc = (s: string) => {
      const str = s.replace(/"/g, '""');
      return /[,"]/.test(str) ? `"${str}"` : str;
    };
    return [tbl.headers.map(esc).join(","), ...tbl.rows.map((r) => r.map(esc).join(","))].join("\n");
  }

  useEffect(() => {
    const saved = localStorage.getItem("kwr:keyword");
    if (saved) setKeyword(saved);
  }, []);

  const analyze = async () => {
    setLoading(true);
    setError(null);
    try {
      addToast("Analyse en cours...", "info");
      const res = await apiFetch("/v1/keywords/research", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keyword }),
      });
      if (!res.ok) throw new Error("Failed to analyze keyword");
      const json = await res.json();
      setResult(json);
      addToast("Keyword analysis completed", "success");
    } catch (e: any) {
      const msg = e.message || "Failed to analyze keyword";
      setError(msg);
      addToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (keyword.trim()) analyze();
  };

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Keyword Research</h1>
      <form onSubmit={handleSubmit} className="space-y-2">
        <Toolbar
          searchValue={keyword}
          onSearchChange={(v) => { setKeyword(v); localStorage.setItem("kwr:keyword", v); }}
          placeholder="Entrez un mot‑clé"
          actions={
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={loading || !keyword.trim()} loading={loading}>Analyser</Button>
              <Button type="button" variant="secondary" disabled={loading} onClick={() => { setKeyword(""); setResult(null); setError(null); localStorage.removeItem("kwr:keyword"); }}>Effacer</Button>
            </div>
          }
        />
        <div className="flex flex-wrap gap-2 text-sm">
          <span className="text-muted">Exemples:</span>
          {SUGGESTIONS.map((s) => (
            <Button key={s} type="button" size="sm" variant="secondary" onClick={() => { setKeyword(s); localStorage.setItem("kwr:keyword", s); }}>
              {s}
            </Button>
          ))}
        </div>
      </form>
      {error && <div className="text-danger">{error}</div>}
      {result && (
        <div className="space-y-4">
          {result.analysis && (
            <Card>
              <CardHeader className="flex items-center justify-between">
                <span>Analysis</span>
                <div className="flex items-center gap-2">
                  <CopyButton text={JSON.stringify(result.analysis, null, 2)} label="Copier" />
                  {toRows(result.analysis) && (
                    <Button size="sm" variant="ghost" onClick={() => {
                      const csv = toCSV(toRows(result.analysis)!);
                      const blob = new Blob([csv], { type: "text/csv" });
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(blob);
                      a.download = `analysis-${Date.now()}.csv`;
                      a.click();
                    }}>CSV</Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const tbl = toRows(result.analysis);
                  if (!tbl) return (
                    <pre className="whitespace-pre-wrap text-sm">{JSON.stringify(result.analysis, null, 2)}</pre>
                  );
                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr>{tbl.headers.map((h) => (<th key={h} className="text-left px-2 py-1 border-b">{h}</th>))}</tr>
                        </thead>
                        <tbody>
                          {tbl.rows.map((r, i) => (
                            <tr key={i}>
                              {r.map((c, j) => (<td key={j} className="px-2 py-1 align-top">{c}</td>))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}
          {result.ideas && (
            <Card>
              <CardHeader className="flex items-center justify-between">
                <span>Keyword Ideas</span>
                <div className="flex items-center gap-2">
                  <CopyButton text={JSON.stringify(result.ideas, null, 2)} label="Copier" />
                  {toRows(result.ideas) && (
                    <Button size="sm" variant="ghost" onClick={() => {
                      const csv = toCSV(toRows(result.ideas)!);
                      const blob = new Blob([csv], { type: "text/csv" });
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(blob);
                      a.download = `ideas-${Date.now()}.csv`;
                      a.click();
                    }}>CSV</Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const tbl = toRows(result.ideas);
                  if (!tbl) return (
                    <pre className="whitespace-pre-wrap text-sm">{JSON.stringify(result.ideas, null, 2)}</pre>
                  );
                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr>{tbl.headers.map((h) => (<th key={h} className="text-left px-2 py-1 border-b">{h}</th>))}</tr>
                        </thead>
                        <tbody>
                          {tbl.rows.map((r, i) => (
                            <tr key={i}>
                              {r.map((c, j) => (<td key={j} className="px-2 py-1 align-top">{c}</td>))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}
          {result.competitors && (
            <Card>
              <CardHeader className="flex items-center justify-between">
                <span>Competitor Analysis</span>
                <div className="flex items-center gap-2">
                  <CopyButton text={JSON.stringify(result.competitors, null, 2)} label="Copier" />
                  {toRows(result.competitors) && (
                    <Button size="sm" variant="ghost" onClick={() => {
                      const csv = toCSV(toRows(result.competitors)!);
                      const blob = new Blob([csv], { type: "text/csv" });
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(blob);
                      a.download = `competitors-${Date.now()}.csv`;
                      a.click();
                    }}>CSV</Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const tbl = toRows(result.competitors);
                  if (!tbl) return (
                    <pre className="whitespace-pre-wrap text-sm">{JSON.stringify(result.competitors, null, 2)}</pre>
                  );
                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr>{tbl.headers.map((h) => (<th key={h} className="text-left px-2 py-1 border-b">{h}</th>))}</tr>
                        </thead>
                        <tbody>
                          {tbl.rows.map((r, i) => (
                            <tr key={i}>
                              {r.map((c, j) => (<td key={j} className="px-2 py-1 align-top">{c}</td>))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}
        </div>
      )}
      <Toasts />
    </div>
  );
}
